/**
 * TwoRowsTwoFullWidth Dashboard Template
 *
 * Grid layout: 2 half-width columns in rows 1-2, full-width in rows 3-4
 * Sections: section_one through section_six
 *
 * Layout:
 * ┌───────────┬───────────┐
 * │     1     │     2     │
 * ├───────────┼───────────┤
 * │     3     │     4     │
 * ├───────────┴───────────┤
 * │          5            │
 * ├───────────────────────┤
 * │          6            │
 * └───────────────────────┘
 *
 * Performance optimizations:
 * - Batch loads all chart data in a single request on mount
 * - Uses ChartDataContext to cache data across tab switches
 *
 * Edit Mode Features (Feature #011):
 * - Drag-and-drop card rearrangement
 * - Visual edit indicators
 * - Card configuration editing
 */

import { useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import ChartFactory from '../../Charts/ChartFactory';
import DraggableCard from '../DraggableCard';
import CardEditModal from '../CardEditModal';
import { useChartData } from '../../../contexts/ChartDataContext';
import { useEditMode } from '../../../contexts/EditModeContext';
import { updateCardConfig } from '../../../services/cardConfigService';
import './TwoRowsTwoFullWidth.css';

// Section keys in order (for card order tracking) - constant outside component
const SECTION_KEYS = [
  'section_one', 'section_two', 'section_three', 'section_four',
  'section_five', 'section_six'
];

const TwoRowsTwoFullWidth = ({ elements, chartsConfig, dashboardId }) => {
  const { loadBatchChartData, hasStaleCache, clearChartCache } = useChartData();
  const { isEditMode, updateLayout, updateCard, currentDashboardId, cardChanges } = useEditMode();
  const intervalRef = useRef(null);

  // Initialize card order from elements
  const initialCardOrder = useMemo(() => {
    return SECTION_KEYS
      .map(key => elements[key])
      .filter(id => id !== null && id !== undefined);
  }, [elements]);

  const [cardOrder, setCardOrder] = useState(initialCardOrder);

  // Card edit modal state
  const [cardEditModalOpen, setCardEditModalOpen] = useState(false);
  const [editingCardId, setEditingCardId] = useState(null);
  const [editingChart, setEditingChart] = useState(null);

  // Sync cardOrder when elements change (e.g., switching dashboards)
  useEffect(() => {
    setCardOrder(initialCardOrder);
  }, [initialCardOrder]);

  // Drag-and-drop sensors (keyboard and pointer)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end event
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setCardOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);

        // Update edit mode context
        updateLayout(newOrder);

        return newOrder;
      });
    }
  };

  // Handle edit button click
  const handleEditCard = (chartId) => {
    const chart = getChartConfig(chartId);

    if (!chart) {
      console.error('Chart not found:', chartId);
      return;
    }

    setEditingCardId(chartId);
    setEditingChart(chart);
    setCardEditModalOpen(true);
  };

  // Handle apply from modal
  const handleApplyCardEdit = async (chartDefinition) => {
    try {
      // Save immediately to backend (Snowflake) so preview shows updated data
      await updateCardConfig(currentDashboardId, editingCardId, chartDefinition);

      // Update card in edit mode context (for tracking unsaved changes)
      updateCard(editingCardId, chartDefinition);

      // Clear cache for this chart and re-load with new configuration
      clearChartCache(editingCardId);
      await loadBatchChartData([editingCardId], dashboardId);

      // Close modal
      setCardEditModalOpen(false);
      setEditingCardId(null);
      setEditingChart(null);
    } catch (error) {
      console.error('Failed to apply card changes:', error);
    }
  };

  // Get chart configuration with pending changes merged in
  const getChartConfig = (chartId) => {
    if (!chartsConfig || !chartId) return null;

    const baseChart = chartsConfig.find((c) => c.chart.id === chartId);
    if (!baseChart) return null;

    if (isEditMode && cardChanges && cardChanges[chartId]) {
      const pendingChart = cardChanges[chartId];
      return {
        ...baseChart,
        chart: {
          ...baseChart.chart,
          ...pendingChart.chart
        }
      };
    }

    return baseChart;
  };

  // Get chart type for a given chart ID
  const getChartType = (chartId) => {
    const chartConfig = getChartConfig(chartId);
    if (!chartConfig) return 'bar_chart';

    return chartConfig.chart?.type || 'bar_chart';
  };

  // Batch load all chart data when component mounts
  useEffect(() => {
    const chartIds = cardOrder.filter((id) => id !== null && id !== undefined);

    if (chartIds.length > 0) {
      loadBatchChartData(chartIds, dashboardId).catch((error) => {
        console.error('Failed to batch load chart data:', error);
      });
    }
  }, [cardOrder, loadBatchChartData, dashboardId]);

  // Periodically check for stale cache and refresh
  useEffect(() => {
    const chartIds = cardOrder.filter((id) => id !== null && id !== undefined);

    if (chartIds.length === 0) return;

    intervalRef.current = setInterval(() => {
      if (hasStaleCache()) {
        console.log('Cache is stale, refreshing dashboard data...');
        loadBatchChartData(chartIds, dashboardId).catch((error) => {
          console.error('Failed to refresh chart data:', error);
        });
      }
    }, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [cardOrder, hasStaleCache, loadBatchChartData, dashboardId]);

  // Render chart in grid item
  const renderChart = (chartId, index) => {
    if (!chartId) return null;

    const chart = (
      <ChartFactory
        chartId={chartId}
        chartType={getChartType(chartId)}
      />
    );

    if (isEditMode) {
      return (
        <DraggableCard key={chartId} id={chartId} onEditClick={handleEditCard}>
          {chart}
        </DraggableCard>
      );
    }

    return chart;
  };

  // Render grid content
  const gridContent = isEditMode ? (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={cardOrder} strategy={rectSortingStrategy}>
        <div className="grid-container">
          {cardOrder.map((chartId, index) => (
            <div key={chartId} className="grid-item">
              {renderChart(chartId, index)}
            </div>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  ) : (
    <div className="grid-container">
      {/* Row 1 - 2 half-width columns */}
      <div className="grid-item section-one">
        {elements.section_one && renderChart(elements.section_one, 0)}
      </div>
      <div className="grid-item section-two">
        {elements.section_two && renderChart(elements.section_two, 1)}
      </div>

      {/* Row 2 - 2 half-width columns */}
      <div className="grid-item section-three">
        {elements.section_three && renderChart(elements.section_three, 2)}
      </div>
      <div className="grid-item section-four">
        {elements.section_four && renderChart(elements.section_four, 3)}
      </div>

      {/* Row 3 - full width */}
      <div className="grid-item section-five full-width">
        {elements.section_five && renderChart(elements.section_five, 4)}
      </div>

      {/* Row 4 - full width */}
      <div className="grid-item section-six full-width">
        {elements.section_six && renderChart(elements.section_six, 5)}
      </div>
    </div>
  );

  return (
    <div className="dashboard-template two-rows-two-full-width">
      {gridContent}

      {/* Card Edit Modal */}
      {cardEditModalOpen && editingChart && currentDashboardId && (
        <CardEditModal
          cardId={editingCardId}
          currentChart={editingChart}
          dashboardId={currentDashboardId}
          onClose={() => setCardEditModalOpen(false)}
          onApply={handleApplyCardEdit}
        />
      )}
    </div>
  );
};

TwoRowsTwoFullWidth.propTypes = {
  elements: PropTypes.shape({
    section_one: PropTypes.string,
    section_two: PropTypes.string,
    section_three: PropTypes.string,
    section_four: PropTypes.string,
    section_five: PropTypes.string,
    section_six: PropTypes.string,
  }).isRequired,
  chartsConfig: PropTypes.array,
  dashboardId: PropTypes.string,
};

TwoRowsTwoFullWidth.defaultProps = {
  chartsConfig: [],
};

export default TwoRowsTwoFullWidth;
